import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  PwmProfileTradingGetByIdDocument,
  PwmProfileTradingListPaginatedDocument,
  PwmProfileTradingCreateDocument,
  PwmProfileTradingUpdateDocument,
  PwmProfileTradingDeleteDocument,
} from "api/generated";
import { Optional } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IPwmProfileTradingCreateVars,
  IPwmProfileTradingModel,
  IPwmProfileTradingPaginatedResponse,
  IPwmProfileTradingPaginatedVars,
  IPwmProfileTradingUpdateVars,
} from "./types";

export async function getPwmProfileTrading(
  id: number
): Promise<Optional<IPwmProfileTradingModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileTradingGetByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileTradingGetById;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileTradingPaginated(
  variables: IPwmProfileTradingPaginatedVars
): Promise<Optional<IPwmProfileTradingPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileTradingListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileTradingListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmProfileTrading(
  variables: IPwmProfileTradingCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileTradingCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict own lists + parent PWM profile entity
    executeEviction(client, {
      typename: "PwmProfileTrading",
      fieldNames: ["pwmProfileTradingListPaginated"],
    });

    executeEviction(client, {
      typename: "PwmProfile",
      id: variables.profileId.toString(),
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileTradingCreate);
  } catch (error) {
    throw error;
  }
}

export async function updatePwmProfileTrading(
  variables: IPwmProfileTradingUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileTradingUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + own lists + getById
    executeEviction(client, {
      typename: "PwmProfileTrading",
      id: variables.id.toString(),
      fieldNames: [
        "pwmProfileTradingListPaginated",
        "pwmProfileTradingGetById",
      ],
    });

    // Evict all parent PWM profile entities (profileId not available)
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileTradingUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deletePwmProfileTrading(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileTradingDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + own lists
    executeEviction(client, {
      typename: "PwmProfileTrading",
      id: id.toString(),
      fieldNames: ["pwmProfileTradingListPaginated"],
    });

    // Evict all parent PWM profile entities (profileId not available)
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileTradingDelete);
  } catch (error) {
    throw error;
  }
}
